import React, { useEffect, useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import classNames from "classnames";
import DatePicker from "react-datepicker";
import datePickerS from "react-datepicker/dist/react-datepicker.css";
import moment, { Moment } from "moment";
import { FormControlLabel, Switch } from "@mui/material";
import {
  ButtonGroup,
  Col,
  Collapse,
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  Button,
} from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Navigate } from "react-router-dom";
import PrimaryButton from "../../../toolkit/primaryButton";
import BaseInput from "../../../toolkit/baseInput";
import * as commonActions from "../../../utils/actions/common";
import {
  SYSTEM_NOTIFICATIONS_RTDB_PATH,
  SYSTEM_NOTIFICATION_MESSAGE_MAX,
  SystemNotificationRecord,
  SystemNotificationType,
  formatSystemNotificationTimestamp,
  parseAllSystemNotifications,
} from "../../../utils/systemNotifications";
import s from "./SystemNotificationsManage.scss";

interface Props {
  isSuperadmin: boolean;
  userId: string;
  userName: string;
  userEmail: string;
  hideSelection: () => void;
}

interface FormState {
  type: SystemNotificationType;
  message: string;
  expiresAt: Moment;
  sendPush: boolean;
  pushTitle: string;
  pushBody: string;
}

function defaultExpiryMoment(): Moment {
  return moment().add(1, "day");
}

const TYPE_OPTIONS: { value: SystemNotificationType; label: string }[] = [
  { value: "warning", label: "Warning" },
  { value: "information", label: "Information" },
  { value: "success", label: "Success" },
];

function typeToggleClass(type: SystemNotificationType, selected: boolean): string {
  const base =
    type === "warning"
      ? s.typeToggleBtnWarning
      : type === "success"
      ? s.typeToggleBtnSuccess
      : s.typeToggleBtnInformation;
  const active =
    type === "warning"
      ? s.typeToggleBtnWarningActive
      : type === "success"
      ? s.typeToggleBtnSuccessActive
      : s.typeToggleBtnInformationActive;
  return classNames(s.typeToggleBtn, base, selected && active);
}

function resolveCreatedBy(userId: string, userName: string, userEmail: string): string {
  if (userEmail.trim()) {
    return userEmail.trim();
  }
  if (userName.trim()) {
    return userName.trim();
  }
  return userId || "superadmin";
}

function SystemNotificationsManageView({
  isSuperadmin,
  userId,
  userName,
  userEmail,
  hideSelection,
}: Props) {
  const [entries, setEntries] = useState(parseAllSystemNotifications(null));
  const [form, setForm] = useState<FormState>(() => ({
    type: "warning",
    message: "",
    expiresAt: defaultExpiryMoment(),
    sendPush: false,
    pushTitle: "",
    pushBody: "",
  }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    hideSelection();
  }, [hideSelection]);

  useEffect(() => {
    const ref = firebase.database().ref(SYSTEM_NOTIFICATIONS_RTDB_PATH);
    const onValue = (snapshot: firebase.database.DataSnapshot) => {
      setEntries(
        parseAllSystemNotifications(
          snapshot.val() as Record<string, SystemNotificationRecord> | null
        )
      );
    };
    ref.on("value", onValue);
    return () => {
      ref.off("value", onValue);
    };
  }, []);

  if (!isSuperadmin) {
    return <Navigate to="/contacts" replace />;
  }

  const validate = (): string | null => {
    const message = form.message.trim();
    if (!message) {
      return "Message is required.";
    }
    if (message.length > SYSTEM_NOTIFICATION_MESSAGE_MAX) {
      return `Message must be at most ${SYSTEM_NOTIFICATION_MESSAGE_MAX} characters.`;
    }
    const expiresAt = form.expiresAt.toDate().getTime();
    if (!Number.isFinite(expiresAt)) {
      return "Expiry date and time is required.";
    }
    if (expiresAt <= Date.now()) {
      return "Expiry must be in the future.";
    }
    if (form.sendPush) {
      if (!form.pushTitle.trim()) {
        return "Push title is required when send push is enabled.";
      }
      if (!form.pushBody.trim()) {
        return "Push description is required when send push is enabled.";
      }
    }
    return null;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const expiresAt = form.expiresAt.toDate().getTime();
      const payload: SystemNotificationRecord = {
        type: form.type,
        message: form.message.trim(),
        createdAt: Date.now(),
        expiresAt,
        createdBy: resolveCreatedBy(userId, userName, userEmail),
      };
      if (form.sendPush) {
        payload.sendPush = true;
        payload.pushTitle = form.pushTitle.trim();
        payload.pushBody = form.pushBody.trim();
      }

      await firebase.database().ref(SYSTEM_NOTIFICATIONS_RTDB_PATH).push(payload);

      setForm({
        type: "warning",
        message: "",
        expiresAt: defaultExpiryMoment(),
        sendPush: false,
        pushTitle: "",
        pushBody: "",
      });
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Could not create notification."
      );
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      await firebase
        .database()
        .ref(`${SYSTEM_NOTIFICATIONS_RTDB_PATH}/${id}`)
        .remove();
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete notification."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const now = Date.now();

  return (
    <main className={s.main}>
      <div className={s.container}>
        <Row>
          <Col xs={12} md={{ size: 8, offset: 2 }}>
            <h2>Notifications</h2>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={{ size: 8, offset: 2 }}>
            <Form onSubmit={onSubmit} className={s.form}>
              <FormGroup className={s.typeGroup}>
                <label className={s.titleLabel}>Type</label>
                <div className={s.typeButtonRow}>
                  <ButtonGroup className={s.typeButtonGroup}>
                    {TYPE_OPTIONS.map(option => (
                      <Button
                        key={option.value}
                        type="button"
                        color="none"
                        className={typeToggleClass(
                          option.value,
                          form.type === option.value
                        )}
                        onClick={() =>
                          setForm(current => ({ ...current, type: option.value }))
                        }
                      >
                        {option.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              </FormGroup>

              <label className={s.titleLabel} htmlFor="system-notification-message">
                Message
              </label>
              <BaseInput
                id="system-notification-message"
                name="system-notification-message"
                dataQAId="system-notification-message"
                dataElmId="system-notification-message"
                type="textarea"
                value={form.message}
                customClassName={s.messageTextArea}
                onChange={e =>
                  setForm(current => ({ ...current, message: e.target.value }))
                }
                onFocus={() => {}}
                errors={[]}
                disabled={false}
                options={[]}
              />

              <FormGroup>
                <label className={s.titleLabel} htmlFor="system-notification-expires">
                  Expires
                </label>
                <InputGroup className={s.dateInputGroup}>
                  <InputGroupText className={s.inputGroup}>
                    <i className={classNames("fa fa-calendar", s.calendarIcon)} />
                  </InputGroupText>
                  <div className={s.datePicker}>
                    <DatePicker
                      id="system-notification-expires"
                      selected={form.expiresAt}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="LLL"
                      timeCaption="Time"
                      minDate={moment()}
                      className={s.dateInput}
                      onChange={date =>
                        setForm(current => ({
                          ...current,
                          expiresAt: date || defaultExpiryMoment(),
                        }))
                      }
                      autoComplete="off"
                    />
                  </div>
                </InputGroup>
              </FormGroup>

              <div className={s.pushRow}>
                <FormControlLabel
                  className={s.switchControl}
                  control={
                    <Switch
                      checked={form.sendPush}
                      onChange={(_, checked) =>
                        setForm(current => ({ ...current, sendPush: checked }))
                      }
                      color="primary"
                    />
                  }
                  label=""
                />
                <span className={s.pushLabel}>
                  Send push notification to all admin mobile apps (iOS and Android)
                </span>
              </div>

              {form.sendPush && (
                <>
                  <label
                    className={s.titleLabel}
                    htmlFor="system-notification-push-title"
                  >
                    Push title
                  </label>
                  <BaseInput
                    id="system-notification-push-title"
                    name="system-notification-push-title"
                    dataQAId="system-notification-push-title"
                    dataElmId="system-notification-push-title"
                    type="text"
                    value={form.pushTitle}
                    onChange={e =>
                      setForm(current => ({
                        ...current,
                        pushTitle: e.target.value,
                      }))
                    }
                    onFocus={() => {}}
                    errors={[]}
                    disabled={false}
                    options={[]}
                  />
                  <label
                    className={s.titleLabel}
                    htmlFor="system-notification-push-body"
                  >
                    Push description
                  </label>
                  <BaseInput
                    id="system-notification-push-body"
                    name="system-notification-push-body"
                    dataQAId="system-notification-push-body"
                    dataElmId="system-notification-push-body"
                    type="textarea"
                    value={form.pushBody}
                    customClassName={s.pushBodyTextArea}
                    onChange={e =>
                      setForm(current => ({
                        ...current,
                        pushBody: e.target.value,
                      }))
                    }
                    onFocus={() => {}}
                    errors={[]}
                    disabled={false}
                    options={[]}
                  />
                </>
              )}

              {error && <p className={s.error}>{error}</p>}

              <div className={s.centered}>
                <PrimaryButton type="submit" loading={busy} disabled={busy}>
                  Create notification
                </PrimaryButton>
              </div>
            </Form>
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={{ size: 8, offset: 2 }}>
            <button
              type="button"
              className={s.historyToggle}
              onClick={() => setHistoryOpen(open => !open)}
              aria-expanded={historyOpen}
            >
              <span>All notifications</span>
              <i
                className={classNames(
                  "fas",
                  historyOpen ? "fa-chevron-up" : "fa-chevron-down"
                )}
              />
            </button>
            <Collapse isOpen={historyOpen}>
              <Table responsive bordered hover className={s.table}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Message</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Push</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={6} className={s.empty}>
                        No notifications yet.
                      </td>
                    </tr>
                  )}
                  {entries.map(({ id, record }) => {
                    const active = record.expiresAt > now;
                    return (
                      <tr key={id} className={active ? s.rowActive : s.rowExpired}>
                        <td>{record.type}</td>
                        <td>{record.message}</td>
                        <td>{formatSystemNotificationTimestamp(record.createdAt)}</td>
                        <td>{formatSystemNotificationTimestamp(record.expiresAt)}</td>
                        <td>
                          {record.sendPush
                            ? record.pushSentAt
                              ? `Sent ${formatSystemNotificationTimestamp(record.pushSentAt)}`
                              : "Requested"
                            : "—"}
                        </td>
                        <td>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            disabled={deletingId === id}
                            onClick={() => onDelete(id)}
                          >
                            {deletingId === id ? "Deleting…" : "Delete"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Collapse>
          </Col>
        </Row>
      </div>
    </main>
  );
}

const mapStateToProps = (state: {
  user: {
    isSuperadmin: boolean;
    id: string;
    name: string;
    email: string;
  };
}) => ({
  isSuperadmin: state.user.isSuperadmin,
  userId: state.user.id,
  userName: state.user.name,
  userEmail: state.user.email,
});

const mapDispatchToProps = {
  hideSelection: commonActions.hideSelection,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(datePickerS, s)(SystemNotificationsManageView)
);
